import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '../ui/Badge';
import {
  WORKFLOW_TAXONOMY_PROMPT_KEYS,
  workflowTypePromptsRecord,
  workflowTypePromptText,
} from './workflow-prompt-fields';

interface WorkflowTypeLike {
  id?: string;
  label?: string;
  executionMode?: string;
  description?: string;
  repoType?: string;
  reviewGate?: string;
  reviewMode?: string;
  prompts?: Record<string, unknown>;
  entityPrompts?: Record<string, unknown>;
  tools?: Array<{
    toolId?: string;
    enabled?: boolean;
    mode?: string;
    capabilities?: string[];
    config?: Record<string, unknown>;
  }>;
}

export function ResolvedWorkflowTaxonomyReadonly({ workflowTypes }: { workflowTypes: WorkflowTypeLike[] }) {
  if (!workflowTypes.length) {
    return <p className="text-xs text-gray-500">No workflow types in merged taxonomy.</p>;
  }

  return (
    <div className="space-y-2">
      {workflowTypes.map((wt, i) => (
        <ReadonlyWorkflowTypeCard key={wt.id || `wt-${i}`} wt={wt} index={i} />
      ))}
    </div>
  );
}

function ReadonlyWorkflowTypeCard({ wt, index }: { wt: WorkflowTypeLike; index: number }) {
  const [open, setOpen] = useState(
    !(String(wt.label || '').trim() || String(wt.id || '').trim()),
  );
  const title = wt.label || wt.id || `Type ${index + 1}`;
  const prompts = workflowTypePromptsRecord(wt);

  return (
    <div className="border border-gray-100 dark:border-gray-700/50 rounded-md bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="flex items-stretch gap-1 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/90 dark:bg-gray-800/50">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex flex-1 items-center gap-2 min-w-0 px-2 py-2 text-left rounded-none hover:bg-gray-100/90 dark:hover:bg-gray-700/40"
        >
          {open ? (
            <ChevronDown className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
          )}
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{title}</span>
          {wt.id ? (
            <Badge variant="outline" className="text-[10px] font-mono shrink-0 max-w-[12rem] truncate">
              {wt.id}
            </Badge>
          ) : null}
        </button>
      </div>
      {open && (
        <div className="px-2 py-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[11px] text-gray-400">ID</span>
              <p className="text-xs text-gray-800 dark:text-gray-200 font-mono">{wt.id || '—'}</p>
            </div>
            <div>
              <span className="text-[11px] text-gray-400">Label</span>
              <p className="text-xs text-gray-800 dark:text-gray-200">{wt.label || '—'}</p>
            </div>
            <div>
              <span className="text-[11px] text-gray-400">Execution mode</span>
              <p className="text-xs text-gray-800 dark:text-gray-200">{String(wt.executionMode ?? '—')}</p>
            </div>
            <div>
              <span className="text-[11px] text-gray-400">Repo type</span>
              <p className="text-xs text-gray-800 dark:text-gray-200">{wt.repoType || '—'}</p>
            </div>
            <div>
              <span className="text-[11px] text-gray-400">Review gate</span>
              <p className="text-xs text-gray-800 dark:text-gray-200">{wt.reviewGate || '—'}</p>
            </div>
            <div>
              <span className="text-[11px] text-gray-400">Review mode</span>
              <p className="text-xs text-gray-800 dark:text-gray-200">{String(wt.reviewMode ?? '—')}</p>
            </div>
            <div className="col-span-2">
              <span className="text-[11px] text-gray-400">Description</span>
              <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{wt.description || '—'}</p>
            </div>
          </div>

          <div className="border border-gray-100 dark:border-gray-700/50 rounded-md p-2 space-y-2">
            <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Workflow prompts
            </span>
            {WORKFLOW_TAXONOMY_PROMPT_KEYS.map((promptKey) => {
              const text = workflowTypePromptText(prompts, promptKey);
              return (
                <div key={promptKey}>
                  <label className="text-[11px] text-gray-400">{promptKey}</label>
                  <textarea
                    readOnly
                    value={text}
                    rows={text.trim() ? Math.min(12, Math.max(2, Math.ceil(text.length / 80))) : 2}
                    className="w-full px-2 py-1 border rounded-md bg-gray-50 dark:bg-gray-950/50 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-xs resize-y mt-0.5"
                  />
                </div>
              );
            })}
          </div>

          <div className="border border-gray-100 dark:border-gray-700/50 rounded-md p-2 space-y-2">
            <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Workflow tools
            </span>
            {!Array.isArray(wt.tools) || wt.tools.length === 0 ? (
              <p className="text-xs text-gray-500">No workflow-specific tool overrides.</p>
            ) : (
              <ul className="space-y-2">
                {wt.tools.map((tool, idx) => (
                  <li key={`${tool.toolId || 'tool'}-${idx}`} className="text-xs border border-gray-200 dark:border-gray-700 rounded p-2">
                    <div className="font-mono text-gray-900 dark:text-gray-100">{tool.toolId || 'unknown-tool'}</div>
                    <div className="text-gray-600 dark:text-gray-400">
                      enabled: {String(tool.enabled ?? false)} · mode: {String(tool.mode || 'overlay')}
                    </div>
                    {Array.isArray(tool.capabilities) && tool.capabilities.length > 0 && (
                      <div className="text-gray-700 dark:text-gray-300 mt-1">
                        capabilities: {tool.capabilities.join(', ')}
                      </div>
                    )}
                    {tool.config && Object.keys(tool.config).length > 0 && (
                      <pre className="mt-1 text-[11px] whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-950/50 rounded p-2">
                        {JSON.stringify(tool.config, null, 2)}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
